import React, { useState, useEffect } from 'react';
import { Building2, Calendar, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { consolidateHoldings, fmtINR } from '../utils/portfolio';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HoldingsReport = ({ transactions, entityInfo, portfolioId, onEnriched }) => {
  const currentDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
  const [priceStats, setPriceStats] = useState({});

  useEffect(() => {
    let active = true;
    let attempts = 0;
    let reloaded = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/holdings/price-stats`, { params: { portfolioId: portfolioId || 'all' } });
        if (!active) return;
        const data = r.data && r.data.stats ? r.data.stats : (r.data || {});
        const pending = r.data && typeof r.data.pending === 'number' ? r.data.pending : 0;
        setPriceStats(data);
        if (!reloaded && Object.keys(data).length > 0 && onEnriched) { reloaded = true; onEnriched(); }
        attempts += 1;
        if (pending > 0 && attempts < 30) setTimeout(poll, 4000);
        else if (pending === 0 && onEnriched) onEnriched();
      } catch (e) {
        attempts += 1;
        if (active && attempts < 30) setTimeout(poll, 4000);
      }
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId, transactions.length]);

  const holdings = consolidateHoldings(transactions).sort((a, b) => b.currentValue - a.currentValue);

  const pctFromHigh = (cur, high) => (high && high > 0 ? ((cur - high) / high) * 100 : null);
  const PctFrom = ({ cur, high, label }) => {
    const v = pctFromHigh(cur, high);
    if (v == null) return null;
    return <div className={`text-xs ${v >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{v >= 0 ? '+' : ''}{v.toFixed(1)}% {label}</div>;
  };

  const summary = holdings.reduce(
    (acc, h) => {
      acc.totalInvested += h.totalCost;
      acc.totalCurrentValue += h.currentValue;
      acc.totalUnrealizedPL += h.profit;
      acc.totalDayChange += h.dayChange;
      return acc;
    },
    { totalInvested: 0, totalCurrentValue: 0, totalUnrealizedPL: 0, totalDayChange: 0 }
  );

  const overallReturn = summary.totalInvested > 0 ? ((summary.totalUnrealizedPL / summary.totalInvested) * 100).toFixed(2) : 0;

  return (
    <Card className="border-2 border-orange-200 shadow-xl bg-gradient-to-br from-white to-orange-50">
      <CardHeader className="border-b-2 border-orange-200 bg-orange-50">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-2xl font-bold text-slate-900 mb-2">Holdings Statement Report</CardTitle>
            <div className="space-y-1 text-sm text-slate-600">
              <div className="flex items-center gap-2"><Building2 className="h-4 w-4" /><span className="font-semibold">{entityInfo.entityName}</span></div>
              <div>Portfolio ID: {entityInfo.portfolioId} | PAN: {entityInfo.panNumber}</div>
              <div className="flex items-center gap-2"><Calendar className="h-4 w-4" /><span>Report Date: {currentDate}</span></div>
            </div>
          </div>
          <Badge className="bg-green-100 text-green-700 text-sm px-3 py-1" data-testid="holdings-stock-count">{holdings.length} Stocks (consolidated)</Badge>
        </div>
      </CardHeader>

      <CardContent className="pt-6">
        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 p-4 bg-white rounded-lg border border-slate-200">
          <div>
            <p className="text-xs text-slate-600 mb-1">Total Invested</p>
            <p className="text-lg font-bold text-slate-900">₹{fmtINR(summary.totalInvested)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-1">Current Value</p>
            <p className="text-lg font-bold text-blue-600">₹{fmtINR(summary.totalCurrentValue)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-1">Unrealized P/L</p>
            <p className={`text-lg font-bold ${summary.totalUnrealizedPL >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>₹{fmtINR(summary.totalUnrealizedPL)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-1">Today's G/L</p>
            <p className={`text-lg font-bold ${summary.totalDayChange >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{summary.totalDayChange >= 0 ? '+' : ''}₹{fmtINR(summary.totalDayChange)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-1">Overall Return</p>
            <p className={`text-lg font-bold flex items-center gap-1 ${overallReturn >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              <TrendingUp className="h-4 w-4" />{overallReturn > 0 ? '+' : ''}{overallReturn}%
            </p>
          </div>
        </div>

        {/* Consolidated Holdings Table */}
        <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100 hover:bg-slate-100">
                  <TableHead className="font-semibold text-slate-700">Scrip Name</TableHead>
                  <TableHead className="font-semibold text-slate-700">Exchange</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Quantity</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Avg. Buy Price</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Prev. Close</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Current Price</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Today's G/L</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">52W High / Low</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">All-Time High / Low</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Invested Value</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Current Value</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">P&L</TableHead>
                  <TableHead className="font-semibold text-slate-700 text-right">Return %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {holdings.map((h) => (
                  <TableRow key={`${h.ticker}-${h.instrumentType}`} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="font-semibold text-slate-900">{h.ticker}</div>
                      <div className="text-xs text-slate-600">{h.shareName}</div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wide">{h.instrumentType}{h.lots > 1 ? ` • ${h.lots} lots` : ''}</div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{h.exchange}</Badge></TableCell>
                    <TableCell className="text-right font-medium text-slate-900">{h.quantity.toLocaleString('en-IN', { maximumFractionDigits: 3 })}</TableCell>
                    <TableCell className="text-right text-slate-700">₹{fmtINR(h.avgPrice)}</TableCell>
                    <TableCell className="text-right text-slate-500">{h.previousClose ? `₹${fmtINR(h.previousClose)}` : '-'}</TableCell>
                    <TableCell className="text-right font-medium text-blue-600">₹{fmtINR(h.currentMarketPrice)}</TableCell>
                    <TableCell className={`text-right font-medium ${h.dayChange > 0 ? 'text-emerald-600' : h.dayChange < 0 ? 'text-rose-600' : 'text-slate-500'}`}>
                      {h.hasPrevClose
                        ? <><div>{h.dayChange > 0 ? '+' : ''}₹{fmtINR(h.dayChange)}</div><div className="text-xs">{(h.dayChangePercent || 0) > 0 ? '+' : ''}{(h.dayChangePercent || 0).toFixed(2)}%</div></>
                        : <span className="text-slate-400">-</span>}
                    </TableCell>
                    <TableCell className="text-right text-slate-700 text-sm">
                      {priceStats[h.ticker]?.week52High != null
                        ? <><div><span className="text-emerald-600">₹{fmtINR(priceStats[h.ticker].week52High)}</span> / <span className="text-rose-600">₹{fmtINR(priceStats[h.ticker].week52Low)}</span></div><PctFrom cur={h.currentMarketPrice} high={priceStats[h.ticker].week52High} label="from high" /></>
                        : <span className="text-slate-400">-</span>}
                    </TableCell>
                    <TableCell className="text-right text-slate-700 text-sm">
                      {priceStats[h.ticker]?.allTimeHigh != null
                        ? <><div><span className="text-emerald-600">₹{fmtINR(priceStats[h.ticker].allTimeHigh)}</span> / <span className="text-rose-600">₹{fmtINR(priceStats[h.ticker].allTimeLow)}</span></div><PctFrom cur={h.currentMarketPrice} high={priceStats[h.ticker].allTimeHigh} label="from ATH" /></>
                        : <span className="text-slate-400">-</span>}
                    </TableCell>
                    <TableCell className="text-right text-slate-700">₹{fmtINR(h.totalCost)}</TableCell>
                    <TableCell className="text-right font-semibold text-blue-600">₹{fmtINR(h.currentValue)}</TableCell>
                    <TableCell className={`text-right font-bold ${h.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{h.profit >= 0 ? '+' : ''}₹{fmtINR(h.profit)}</TableCell>
                    <TableCell className={`text-right font-bold ${h.returnPercentage >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{h.returnPercentage > 0 ? '+' : ''}{h.returnPercentage.toFixed(2)}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {holdings.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <p className="text-lg">No holdings found</p>
            <p className="text-sm mt-1">All positions have been sold</p>
          </div>
        )}

        <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <p className="text-xs text-slate-600">
            <strong>Note:</strong> Holdings are consolidated stock-wise (multiple buy lots of the same stock are combined). Statement as of {currentDate}. Current prices are indicative; verify with your broker.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default HoldingsReport;
